import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  usePostSources,
  usePostSourcesTestConnection,
  usePostSourcesBcTestConnection,
  usePostSourcesSalesforceTestConnection,
  getProfilesById,
  getGetProfilesByIdQueryKey,
} from '@/api/control/control-plane/control-plane';
import { WizardStepper } from '@/components/profiles/wizard/WizardStepper';
import { getWizardSteps, WIZARD_STEP_IDS } from '@/components/profiles/wizard/wizardSteps';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConnectionTestAlert } from '@/components/common/ConnectionTestAlert';
import { Button } from '@/components/ui/button';
import { logger } from '@/services/logger';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useError } from '@/contexts/ErrorContext';
import { LoadingButton } from '@/components/LoadingButton';
import { AdvancedSettingsSection } from '@/components/profiles/settings/AdvancedSettingsSection';
import {
  SourcePageSizeField,
  pageSizeBounds,
  isPageSizeValid,
} from '@/components/profiles/settings/SourcePageSizeField';
import {
  DataverseColumnLengthsFields,
  hasOutOfRangeDataverseLength,
  DEFAULT_DATAVERSE_LENGTHS,
  type DataverseColumnLengthsValue,
} from '@/components/profiles/settings/DataverseColumnLengthsFields';
import type { BcCompanyDto } from '@/api/control/missionImpactAPI.schemas';
import {
  getWizardCredentialLabels,
  type SourceCredentialType,
} from '@/utils/sourceCredentialLabels';

/** Source type discriminator used in the form */
type SourceType = SourceCredentialType;

/**
 * Step 2: Add Data Source
 * Second step in the profile creation wizard.
 * Supports both Dataverse and Business Central source types.
 */
export default function AddSourceStep() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useLanguage();
  const { handleApiError } = useError();
  const { orgId, profileId } = useParams<{ orgId: string; profileId: string }>();

  if (!orgId || !profileId) {
    logger.error(new Error('Missing orgId or profileId in route params'), {
      context: 'AddSourceStep.mount',
    });
    void navigate('/dashboard');
    return null;
  }

  const wizardSteps = getWizardSteps(WIZARD_STEP_IDS.SOURCE);

  // Source type selection
  const [sourceType, setSourceType] = useState<SourceType>('Dataverse');

  // Dataverse fields
  const [baseUrl, setBaseUrl] = useState('');

  // Per-source extraction page size (advanced). Reset to the selected connector's default whenever
  // the source type changes; validated against that connector's range on submit.
  const [pageSize, setPageSize] = useState(pageSizeBounds('Dataverse').default);

  // Dataverse synthetic-column lengths (advanced, Dataverse only). Reset on source-type change.
  const [columnLengths, setColumnLengths] = useState<DataverseColumnLengthsValue>({
    ...DEFAULT_DATAVERSE_LENGTHS,
  });

  // Shared fields (both source types)
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  // Business Central fields
  const [tenantId, setTenantId] = useState('');
  const [bcEnvironment, setBcEnvironment] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [availableCompanies, setAvailableCompanies] = useState<BcCompanyDto[]>([]);

  // Salesforce fields
  const [sfInstanceUrl, setSfInstanceUrl] = useState('');
  const [sfIsSandbox, setSfIsSandbox] = useState(false);

  // Shared state
  const [validationError, setValidationError] = useState<string | null>(null);
  const [connectionTestSuccess, setConnectionTestSuccess] = useState(false);
  const [connectionTestMessage, setConnectionTestMessage] = useState<string | null>(null);
  const [connectionTestError, setConnectionTestError] = useState<string | null>(null);

  // Fetch profile to verify it exists
  const { data: profileData, isLoading: isLoadingProfile } = useQuery({
    queryKey: getGetProfilesByIdQueryKey(orgId, profileId),
    queryFn: async () => {
      const response = await getProfilesById(orgId, profileId);
      return response;
    },
    retry: 1,
  });

  // Redirect if profile doesn't exist or if source already exists (auto-skip to next step)
  useEffect(() => {
    if (!isLoadingProfile && profileData?.data) {
      // If source already exists, skip to target step
      if (profileData.data.source) {
        void navigate(`/dashboard/orgs/${orgId}/profiles/${profileId}/setup/target`);
      }
    } else if (!isLoadingProfile && !profileData?.data) {
      void navigate(`/dashboard/orgs/${orgId}/profiles/wizard/profile`);
    }
  }, [isLoadingProfile, profileData, navigate, orgId, profileId]);

  /** Resets connection test state whenever credentials change */
  const resetConnectionState = () => {
    setConnectionTestSuccess(false);
    setConnectionTestMessage(null);
    setConnectionTestError(null);
    setAvailableCompanies([]);
    setSelectedCompanyId('');
    setSelectedCompanyName('');
  };

  /** Handles source type change, clearing all field values */
  const handleSourceTypeChange = (value: SourceType) => {
    setSourceType(value);
    setBaseUrl('');
    setPageSize(pageSizeBounds(value).default);
    setColumnLengths({ ...DEFAULT_DATAVERSE_LENGTHS });
    setClientId('');
    setClientSecret('');
    setTenantId('');
    setBcEnvironment('');
    setSfInstanceUrl('');
    setSfIsSandbox(false);
    resetConnectionState();
    setValidationError(null);
  };

  // Dataverse test connection mutation
  const { mutate: testDataverseConnection, isPending: isTestingDataverse } =
    usePostSourcesTestConnection({
      mutation: {
        onSuccess: () => {
          setConnectionTestSuccess(true);
          setConnectionTestMessage(t.wizard.source.connectionSuccessful);
          setConnectionTestError(null);
        },
        onError: (error) => {
          setConnectionTestSuccess(false);
          setConnectionTestMessage(null);
          const { message } = handleApiError(
            error,
            { context: 'AddSourceStep.testDataverseConnection', profile: profileData?.data },
            { showToast: false },
          );
          setConnectionTestError(message);
        },
      },
    });

  // Business Central test connection mutation
  const { mutate: testBcConnection, isPending: isTestingBc } = usePostSourcesBcTestConnection({
    mutation: {
      onSuccess: (response) => {
        const companies = response.data.companies;
        setAvailableCompanies(companies);
        setConnectionTestSuccess(true);
        setConnectionTestMessage(t.wizard.source.connectionSuccessful);
        setConnectionTestError(null);
      },
      onError: (error) => {
        setConnectionTestSuccess(false);
        setConnectionTestMessage(null);
        setAvailableCompanies([]);
        const { message } = handleApiError(
          error,
          { context: 'AddSourceStep.testBcConnection', profile: profileData?.data },
          { showToast: false },
        );
        setConnectionTestError(message);
      },
    },
  });

  // Salesforce test connection mutation
  const { mutate: testSalesforceConnection, isPending: isTestingSalesforce } =
    usePostSourcesSalesforceTestConnection({
      mutation: {
        onSuccess: () => {
          setConnectionTestSuccess(true);
          setConnectionTestMessage(t.wizard.source.connectionSuccessful);
          setConnectionTestError(null);
        },
        onError: (error) => {
          setConnectionTestSuccess(false);
          setConnectionTestMessage(null);
          const { message } = handleApiError(
            error,
            { context: 'AddSourceStep.testSalesforceConnection', profile: profileData?.data },
            { showToast: false },
          );
          setConnectionTestError(message);
        },
      },
    });

  const isTesting = isTestingDataverse || isTestingBc || isTestingSalesforce;

  // Create source mutation
  const { mutate: createSource, isPending: isCreating } = usePostSources({
    mutation: {
      onSuccess: () => {
        toast.success(t.sources.notifications.createSuccess);
        logger.trackEvent('Source.Created', {
          orgId,
          profileId,
        });

        // Invalidate queries
        void queryClient.invalidateQueries({
          queryKey: ['sources', 'by-profile', profileId],
        });
        void queryClient.invalidateQueries({
          queryKey: getGetProfilesByIdQueryKey(orgId, profileId),
        });

        // Navigate to step 3: Add Target
        void navigate(`/dashboard/orgs/${orgId}/profiles/${profileId}/setup/target`);
      },
      onError: (error) => {
        const { message } = handleApiError(error, {
          context: 'AddSourceStep.createSource',
          profile: profileData?.data,
        });
        setValidationError('Failed to create source. ' + message);
      },
    },
  });

  /** Handles the test connection button click for Dataverse */
  const handleTestDataverseConnection = () => {
    setValidationError(null);
    setConnectionTestMessage(null);
    setConnectionTestError(null);
    setConnectionTestSuccess(false);

    if (!baseUrl.trim() || !clientId.trim() || !clientSecret.trim()) {
      setValidationError(t.wizard.source.allFieldsRequired);
      return;
    }

    testDataverseConnection({
      orgId,
      profileId,
      data: {
        baseUrl: baseUrl.trim(),
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
      },
    });
  };

  /** Handles the test connection button click for Business Central */
  const handleTestBcConnection = () => {
    setValidationError(null);
    setConnectionTestMessage(null);
    setConnectionTestError(null);
    setConnectionTestSuccess(false);
    setAvailableCompanies([]);
    setSelectedCompanyId('');
    setSelectedCompanyName('');

    if (!tenantId.trim() || !bcEnvironment.trim() || !clientId.trim() || !clientSecret.trim()) {
      setValidationError(t.wizard.source.allFieldsRequired);
      return;
    }

    testBcConnection({
      orgId,
      profileId,
      data: {
        tenantId: tenantId.trim(),
        environment: bcEnvironment.trim(),
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
      },
    });
  };

  /** Handles the test connection button click for Salesforce */
  const handleTestSalesforceConnection = () => {
    setValidationError(null);
    setConnectionTestMessage(null);
    setConnectionTestError(null);
    setConnectionTestSuccess(false);

    if (!sfInstanceUrl.trim() || !clientId.trim() || !clientSecret.trim()) {
      setValidationError(t.wizard.source.allFieldsRequired);
      return;
    }

    testSalesforceConnection({
      orgId,
      profileId,
      data: {
        instanceUrl: sfInstanceUrl.trim(),
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        isSandbox: sfIsSandbox,
      },
    });
  };

  /** Handles company selection from the dropdown */
  const handleCompanySelect = (companyId: string) => {
    const company = availableCompanies.find((c) => c.id === companyId);
    setSelectedCompanyId(companyId);
    setSelectedCompanyName(company?.name ?? '');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    if (!connectionTestSuccess) {
      setValidationError(t.wizard.source.testBeforeProceeding);
      return;
    }

    if (!isPageSizeValid(sourceType, pageSize)) {
      setValidationError(t.wizard.source.pageSizeRange);
      return;
    }

    if (sourceType === 'Dataverse' && hasOutOfRangeDataverseLength(columnLengths)) {
      setValidationError(t.wizard.profile.advancedLengthsInvalid);
      return;
    }

    if (sourceType === 'BusinessCentral' && !selectedCompanyId) {
      setValidationError(t.wizard.source.selectCompanyBeforeProceeding);
      return;
    }

    const config =
      sourceType === 'Dataverse'
        ? {
            $type: 'Dataverse' as const,
            baseUrl: baseUrl.trim(),
            clientId: clientId.trim(),
            clientSecret: clientSecret.trim(),
            pageSize,
            dataverseLookupLabelMaxLength:
              columnLengths.dataverseLookupLabelMaxLength ??
              DEFAULT_DATAVERSE_LENGTHS.dataverseLookupLabelMaxLength,
            dataverseChoiceLabelMaxLength:
              columnLengths.dataverseChoiceLabelMaxLength ??
              DEFAULT_DATAVERSE_LENGTHS.dataverseChoiceLabelMaxLength,
            dataverseMultiSelectMaxLength:
              columnLengths.dataverseMultiSelectMaxLength ??
              DEFAULT_DATAVERSE_LENGTHS.dataverseMultiSelectMaxLength,
          }
        : sourceType === 'BusinessCentral'
          ? {
              $type: 'BusinessCentral' as const,
              tenantId: tenantId.trim(),
              environment: bcEnvironment.trim(),
              clientId: clientId.trim(),
              clientSecret: clientSecret.trim(),
              companyId: selectedCompanyId,
              companyName: selectedCompanyName,
              pageSize,
            }
          : {
              $type: 'Salesforce' as const,
              instanceUrl: sfInstanceUrl.trim(),
              clientId: clientId.trim(),
              clientSecret: clientSecret.trim(),
              isSandbox: sfIsSandbox,
              pageSize,
            };

    createSource({ orgId, profileId, data: { config } });
  };

  // Credential field labels differ per source type (Salesforce uses Consumer Key / Secret)
  const credentialLabels = getWizardCredentialLabels(sourceType, t);

  /** Whether the Next button should be enabled */
  const isNextEnabled =
    connectionTestSuccess &&
    (sourceType !== 'BusinessCentral' || !!selectedCompanyId);

  if (isLoadingProfile) {
    return (
      <div className="space-y-6">
        <WizardStepper steps={wizardSteps} />
        <div className="flex justify-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Wizard Stepper */}
      <WizardStepper steps={wizardSteps} />

      {/* Page Header */}
      <div className="mx-auto text-center">
        <h1 className="text-3xl font-bold">{t.wizard.source.title}</h1>
        <p className="text-muted-foreground">{t.wizard.source.description}</p>
      </div>

      {/* Form Card */}
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>{t.wizard.source.cardTitle}</CardTitle>
          <CardDescription>{t.wizard.source.cardDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Source Type Selector */}
            <div className="space-y-2">
              <Label htmlFor="sourceType">{t.wizard.source.sourceType}</Label>
              <Select
                value={sourceType}
                onValueChange={(v) => { handleSourceTypeChange(v as SourceType); }}
              >
                <SelectTrigger id="sourceType">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Dataverse">{t.wizard.source.dataverse}</SelectItem>
                  <SelectItem value="BusinessCentral">{t.wizard.source.businessCentral}</SelectItem>
                  <SelectItem value="Salesforce">{t.wizard.source.salesforce}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Dataverse Fields */}
            {sourceType === 'Dataverse' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="baseUrl">{t.wizard.source.baseUrl}</Label>
                  <Input
                    id="baseUrl"
                    type="url"
                    value={baseUrl}
                    onChange={(e) => {
                      setBaseUrl(e.target.value);
                      resetConnectionState();
                    }}
                    placeholder={t.wizard.source.baseUrlPlaceholder}
                    required
                  />
                </div>
              </>
            )}

            {/* Business Central Fields */}
            {sourceType === 'BusinessCentral' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="tenantId">{t.wizard.source.tenantId}</Label>
                  <Input
                    id="tenantId"
                    value={tenantId}
                    onChange={(e) => {
                      setTenantId(e.target.value);
                      resetConnectionState();
                    }}
                    placeholder={t.wizard.source.tenantIdPlaceholder}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bcEnvironment">{t.wizard.source.bcEnvironment}</Label>
                  <Input
                    id="bcEnvironment"
                    value={bcEnvironment}
                    onChange={(e) => {
                      setBcEnvironment(e.target.value);
                      resetConnectionState();
                    }}
                    placeholder={t.wizard.source.bcEnvironmentPlaceholder}
                    required
                  />
                </div>
              </>
            )}

            {/* Salesforce Fields */}
            {sourceType === 'Salesforce' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="sfInstanceUrl">{t.wizard.source.instanceUrl}</Label>
                  <Input
                    id="sfInstanceUrl"
                    type="url"
                    value={sfInstanceUrl}
                    onChange={(e) => {
                      setSfInstanceUrl(e.target.value);
                      resetConnectionState();
                    }}
                    placeholder={t.wizard.source.instanceUrlPlaceholder}
                    required
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="sfIsSandbox"
                    checked={sfIsSandbox}
                    onCheckedChange={(checked) => {
                      setSfIsSandbox(checked === true);
                      resetConnectionState();
                    }}
                  />
                  <div>
                    <Label htmlFor="sfIsSandbox" className="cursor-pointer">
                      {t.wizard.source.isSandbox}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t.wizard.source.isSandboxDescription}
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* Shared Fields: Client ID (Consumer Key for Salesforce) */}
            <div className="space-y-2">
              <Label htmlFor="clientId">{credentialLabels.clientId}</Label>
              <Input
                id="clientId"
                value={clientId}
                onChange={(e) => {
                  setClientId(e.target.value);
                  resetConnectionState();
                }}
                placeholder={credentialLabels.clientIdPlaceholder}
                required
              />
              {sourceType === 'Dataverse' && (
                <p className="text-xs text-muted-foreground">{t.sources.sourceCapacity.dedicatedAppNote}</p>
              )}
            </div>

            {/* Shared Fields: Client Secret (Secret for Salesforce) */}
            <div className="space-y-2">
              <Label htmlFor="clientSecret">{credentialLabels.clientSecret}</Label>
              <Input
                id="clientSecret"
                type="password"
                value={clientSecret}
                onChange={(e) => {
                  setClientSecret(e.target.value);
                  resetConnectionState();
                }}
                placeholder={credentialLabels.clientSecretPlaceholder}
                required
              />
            </div>

            {/* Advanced (replication-impacting) per-source settings — opt-in. */}
            <AdvancedSettingsSection
              title={t.wizard.advancedSettings.title}
              description={t.wizard.advancedSettings.description}
            >
              <SourcePageSizeField
                sourceType={sourceType}
                value={pageSize}
                onChange={setPageSize}
              />
              {sourceType === 'Dataverse' && (
                <DataverseColumnLengthsFields
                  value={columnLengths}
                  onChange={setColumnLengths}
                />
              )}
            </AdvancedSettingsSection>

            {/* Test Connection Button — hidden for BC once companies are loaded */}
            {!(sourceType === 'BusinessCentral' && connectionTestSuccess) && (
              <LoadingButton
                type="button"
                variant="outline"
                onClick={
                  sourceType === 'Dataverse'
                    ? handleTestDataverseConnection
                    : sourceType === 'BusinessCentral'
                      ? handleTestBcConnection
                      : handleTestSalesforceConnection
                }
                isLoading={isTesting}
                disabled={
                  sourceType === 'Dataverse'
                    ? !baseUrl.trim() || !clientId.trim() || !clientSecret.trim()
                    : sourceType === 'BusinessCentral'
                      ? !tenantId.trim() ||
                        !bcEnvironment.trim() ||
                        !clientId.trim() ||
                        !clientSecret.trim()
                      : !sfInstanceUrl.trim() || !clientId.trim() || !clientSecret.trim()
                }
                className="w-full"
              >
                {sourceType === 'Dataverse' || sourceType === 'Salesforce'
                  ? t.wizard.source.testConnection
                  : t.wizard.source.testConnectionAndLoadCompanies}
              </LoadingButton>
            )}

            {/* Business Central Company Selector */}
            {sourceType === 'BusinessCentral' && connectionTestSuccess && (
              <div className="space-y-2">
                <Label htmlFor="company">{t.wizard.source.company}</Label>
                <div className="flex gap-2">
                  <Select
                    value={selectedCompanyId}
                    onValueChange={handleCompanySelect}
                  >
                    <SelectTrigger id="company" className="flex-1">
                      <SelectValue placeholder={t.wizard.source.companyPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCompanies.length === 0 ? (
                        <SelectItem value="__none__" disabled>
                          {t.wizard.source.noCompaniesFound}
                        </SelectItem>
                      ) : (
                        availableCompanies.map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            {company.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={handleTestBcConnection}
                    disabled={isTestingBc}
                    title={t.wizard.source.testConnectionAndLoadCompanies}
                  >
                    {isTestingBc
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <RefreshCw className="h-4 w-4" />
                    }
                  </Button>
                </div>
              </div>
            )}

            {/* Connection Test Result */}
            {(connectionTestSuccess || connectionTestError) && (
              <ConnectionTestAlert
                success={connectionTestSuccess}
                message={connectionTestError ?? connectionTestMessage ?? ''}
              />
            )}

            {/* Validation Error */}
            {validationError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{validationError}</AlertDescription>
              </Alert>
            )}

            {/* Actions */}
            <div className="flex justify-end pt-4">
              <LoadingButton type="submit" disabled={!isNextEnabled} isLoading={isCreating}>
                {t.wizard.source.next}
              </LoadingButton>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
